import { useState, useCallback, useMemo } from 'react';
import { PracticeProjectFilters } from '../../../types/practiceProjects';

export interface FilterState extends PracticeProjectFilters {
  page: number;
  limit: number;
}

const defaultFilters: FilterState = {
  search: '',
  difficulty_level: '',
  status: '',
  tags: [],
  course_id: '',
  page: 1,
  limit: 10,
};

export function usePracticeProjectFilters(initialFilters: Partial<FilterState> = {}) {
  const [filters, setFilters] = useState<FilterState>({
    ...defaultFilters,
    ...initialFilters,
  });

  const updateFilter = useCallback((key: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when non-pagination filters change
      ...(key !== 'page' && key !== 'limit' ? { page: 1 } : {}),
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleQuickSearch = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  // Build API filters without empty values
  const apiFilters = useMemo(() => {
    const result: any = {
      page: filters.page,
      limit: filters.limit,
    };
    
    if (filters.search) result.search = filters.search;
    if (filters.difficulty_level) result.difficulty_level = filters.difficulty_level;
    if (filters.status) result.status = filters.status;
    if (filters.tags.length > 0) result.tags = filters.tags;
    if (filters.course_id) result.course_id = filters.course_id;
    
    return result;
  }, [filters]);

  return {
    filters,
    apiFilters,
    updateFilter,
    resetFilters,
    handlePageChange,
    handleQuickSearch,
    handleFiltersChange,
  };
}
